import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import api from 'api';
import type {
    ProfileDate,
    SpouseEducationProfile,
    SpouseLanguageProfile,
    SpouseProfile,
    SpouseWorkExperienceProfile,
} from 'api/models/response/SpouseProfile';

const STALE_5_MIN = 5 * 60 * 1000;

export type SpouseProfileSection = 'language' | 'education' | 'work-experience';

export const spouseProfileKeys = {
    base: (candidateId?: number) => ['profile', candidateId ?? 'me', 'spouse'] as const,
    section: (candidateId: number | undefined, section: SpouseProfileSection) =>
        ['profile', candidateId ?? 'me', 'spouse', section] as const,
};

interface UseSpouseProfileOptions {
    enabled?: boolean;
    section?: SpouseProfileSection;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapData = (value: unknown): unknown => {
    if (isRecord(value) && 'data' in value) {
        return value.data;
    }

    return value;
};

const unwrapSection = (value: unknown, section: string): unknown => {
    const unwrapped = unwrapData(value);

    if (isRecord(unwrapped) && section in unwrapped) {
        return unwrapped[section];
    }

    return unwrapped;
};

const readValue = (record: UnknownRecord, ...keys: string[]): unknown => {
    for (const key of keys) {
        if (key in record) {
            return record[key];
        }
    }

    return undefined;
};

const asText = (value: unknown): string | null => {
    if (typeof value === 'string') {
        return value.trim() || null;
    }

    if (typeof value === 'number') {
        return String(value);
    }

    return null;
};

const asDate = (value: unknown): ProfileDate => {
    if (typeof value === 'string') {
        return value.trim() || null;
    }

    if (value instanceof Date) {
        return value;
    }

    return null;
};

const asNumberOrText = (value: unknown): number | string | null => {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value === 'string') {
        return value.trim() || null;
    }

    return null;
};

const asUserId = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return value;
    }

    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }

    return null;
};

/**
 * Profile V0 `Spouse` keeps `user_id` as the owning candidate and strips row `id`.
 * The navigable spouse User id comes from `SpouseNew` (related User) or explicit
 * related-* fields when present.
 */
const resolveRelatedUserId = (
    candidate: UnknownRecord,
    viewingCandidateId?: number
): number | null => {
    const explicit =
        asUserId(readValue(candidate, 'related_user_id')) ??
        asUserId(readValue(candidate, 'spouse_user_id')) ??
        asUserId(readValue(candidate, 'partner_user_id')) ??
        asUserId(readValue(candidate, 'partner_id')) ??
        asUserId(readValue(candidate, 'related_id'));

    if (explicit && explicit !== viewingCandidateId) {
        return explicit;
    }

    for (const nestedKey of ['related_user', 'relatedUser', 'spouse_user', 'user', 'User']) {
        const nested = readValue(candidate, nestedKey);
        if (!isRecord(nested)) {
            continue;
        }

        const nestedId =
            asUserId(readValue(nested, 'id')) ?? asUserId(readValue(nested, 'user_id'));
        if (nestedId && nestedId !== viewingCandidateId) {
            return nestedId;
        }
    }

    return null;
};

const resolveRelatedUserIdFromSpouseNew = (
    value: unknown,
    viewingCandidateId?: number
): number | null => {
    const unwrapped = unwrapData(value);
    const candidate = Array.isArray(unwrapped)
        ? unwrapped.find((item) => isRecord(item) && Object.keys(item).length > 0)
        : unwrapped;

    if (!isRecord(candidate)) {
        return null;
    }

    const relatedId = asUserId(readValue(candidate, 'id'));
    if (relatedId && relatedId !== viewingCandidateId) {
        return relatedId;
    }

    return null;
};

const collectCitizenships = (value: unknown): string[] => {
    const citizenships = new Set<string>();

    const collect = (item: unknown) => {
        if (Array.isArray(item)) {
            item.forEach(collect);
            return;
        }

        if (isRecord(item)) {
            collect(readValue(item, 'country', 'name', 'nationality'));
            return;
        }

        const text = asText(item);
        if (text) {
            citizenships.add(text);
        }
    };

    collect(value);
    return Array.from(citizenships);
};

export const normalizeSpouseProfile = (
    value: unknown,
    viewingCandidateId?: number
): SpouseProfile | null => {
    const unwrapped = unwrapSection(value, 'Spouse');
    const candidate = Array.isArray(unwrapped)
        ? unwrapped.find((item) => isRecord(item) && Object.keys(item).length > 0)
        : unwrapped;

    if (!isRecord(candidate) || Object.keys(candidate).length === 0) {
        return null;
    }

    const citizenships = collectCitizenships(
        readValue(candidate, 'citizenships', 'citizenship', 'Citizenship')
    );

    const spouse: SpouseProfile = {
        user_id: resolveRelatedUserId(candidate, viewingCandidateId),
        firstname: asText(readValue(candidate, 'firstname', 'first_name')),
        lastname: asText(readValue(candidate, 'lastname', 'last_name')),
        middlename: asText(readValue(candidate, 'middlename', 'middle_name')),
        email: asText(readValue(candidate, 'email')),
        phone_number: asText(readValue(candidate, 'phone_number', 'phone')),
        birth_date: asDate(readValue(candidate, 'birth_date')),
        gender: asText(readValue(candidate, 'gender')),
        nationality: asText(readValue(candidate, 'nationality')) ?? citizenships[0] ?? null,
        citizenships,
        country_of_residence: asText(readValue(candidate, 'country_of_residence', 'country')),
        region_of_residence: asText(readValue(candidate, 'region_of_residence', 'region')),
        residency_status: asText(readValue(candidate, 'residency_status')),
        residence_since: asDate(readValue(candidate, 'residence_since')),
        income: asNumberOrText(readValue(candidate, 'income')),
    };

    return Object.keys(candidate).length > 0 ? spouse : null;
};

/** Fill null fields from a fallback so an empty GET Spouse shell does not hide candidate-profile data. */
export const mergeSpouseProfiles = (
    preferred: SpouseProfile | null,
    fallback: SpouseProfile | null
): SpouseProfile | null => {
    if (!preferred) {
        return fallback;
    }
    if (!fallback) {
        return preferred;
    }
    return {
        user_id: preferred.user_id ?? fallback.user_id,
        firstname: preferred.firstname ?? fallback.firstname,
        lastname: preferred.lastname ?? fallback.lastname,
        middlename: preferred.middlename ?? fallback.middlename,
        email: preferred.email ?? fallback.email,
        phone_number: preferred.phone_number ?? fallback.phone_number,
        birth_date: preferred.birth_date ?? fallback.birth_date,
        gender: preferred.gender ?? fallback.gender,
        nationality: preferred.nationality ?? fallback.nationality,
        citizenships:
            preferred.citizenships.length > 0 ? preferred.citizenships : fallback.citizenships,
        country_of_residence: preferred.country_of_residence ?? fallback.country_of_residence,
        region_of_residence: preferred.region_of_residence ?? fallback.region_of_residence,
        residency_status: preferred.residency_status ?? fallback.residency_status,
        residence_since: preferred.residence_since ?? fallback.residence_since,
        income: preferred.income ?? fallback.income,
    };
};

const normalizeList = <T>(value: unknown, section: string): T[] => {
    const unwrapped = unwrapSection(value, section);

    return Array.isArray(unwrapped) ? (unwrapped.filter(isRecord) as T[]) : [];
};

const isNotFound = (error: unknown) => isAxiosError(error) && error.response?.status === 404;

export function useSpouseProfile(
    candidateId?: number,
    { enabled = true, section }: UseSpouseProfileOptions = {}
) {
    const queryClient = useQueryClient();
    const baseQuery = useQuery<SpouseProfile | null>({
        queryKey: spouseProfileKeys.base(candidateId),
        queryFn: async () => {
            const { data } = await api.spouseProfile.getSpouse(candidateId);
            let spouse = normalizeSpouseProfile(data, candidateId);

            // Spouse.user_id is the owner; SpouseNew returns the related User with navigable id.
            try {
                const { data: relatedUser } =
                    await api.spouseProfile.getSpouseRelatedUser(candidateId);
                const relatedUserId = resolveRelatedUserIdFromSpouseNew(relatedUser, candidateId);
                const relatedProfile = normalizeSpouseProfile(relatedUser, candidateId);

                if (relatedUserId) {
                    spouse = {
                        ...(relatedProfile ??
                            spouse ?? {
                                user_id: null,
                                firstname: null,
                                lastname: null,
                                middlename: null,
                                email: null,
                                phone_number: null,
                                birth_date: null,
                                gender: null,
                                nationality: null,
                                citizenships: [],
                                country_of_residence: null,
                                region_of_residence: null,
                                residency_status: null,
                                residence_since: null,
                                income: null,
                            }),
                        // Prefer Spouse assessment fields when present; always set navigable id.
                        ...(spouse ?? {}),
                        user_id: relatedUserId,
                        firstname: spouse?.firstname ?? relatedProfile?.firstname ?? null,
                        lastname: spouse?.lastname ?? relatedProfile?.lastname ?? null,
                        email: spouse?.email ?? relatedProfile?.email ?? null,
                        phone_number: spouse?.phone_number ?? relatedProfile?.phone_number ?? null,
                    };
                }
            } catch (error) {
                if (!isNotFound(error)) {
                    throw error;
                }
            }

            return spouse;
        },
        enabled,
        staleTime: STALE_5_MIN,
    });

    const hasSpouse = baseQuery.data !== null && baseQuery.data !== undefined;
    const sectionEnabled = enabled && hasSpouse;

    const languageQuery = useQuery<SpouseLanguageProfile[]>({
        queryKey: spouseProfileKeys.section(candidateId, 'language'),
        queryFn: async () => {
            try {
                const { data } = await api.spouseProfile.getSpouseLanguage(candidateId);
                return normalizeList<SpouseLanguageProfile>(data, 'SpouseLanguage');
            } catch (error) {
                if (isNotFound(error)) {
                    return [];
                }

                throw error;
            }
        },
        enabled: sectionEnabled && section === 'language',
        staleTime: STALE_5_MIN,
    });

    const educationQuery = useQuery<SpouseEducationProfile[]>({
        queryKey: spouseProfileKeys.section(candidateId, 'education'),
        queryFn: async () => {
            try {
                const { data } = await api.spouseProfile.getSpouseEducation(candidateId);
                return normalizeList<SpouseEducationProfile>(data, 'SpouseEducation');
            } catch (error) {
                if (isNotFound(error)) {
                    return [];
                }

                throw error;
            }
        },
        enabled: sectionEnabled && section === 'education',
        staleTime: STALE_5_MIN,
    });

    const workExperienceQuery = useQuery<SpouseWorkExperienceProfile[]>({
        queryKey: spouseProfileKeys.section(candidateId, 'work-experience'),
        queryFn: async () => {
            try {
                const { data } = await api.spouseProfile.getSpouseWorkExperience(candidateId);
                return normalizeList<SpouseWorkExperienceProfile>(data, 'SpouseWorkExperience');
            } catch (error) {
                if (isNotFound(error)) {
                    return [];
                }

                throw error;
            }
        },
        enabled: sectionEnabled && section === 'work-experience',
        staleTime: STALE_5_MIN,
    });

    const activeSectionQuery =
        section === 'language'
            ? languageQuery
            : section === 'education'
              ? educationQuery
              : section === 'work-experience'
                ? workExperienceQuery
                : null;

    return {
        spouse: baseQuery.data ?? null,
        language: languageQuery.data ?? [],
        education: educationQuery.data ?? [],
        workExperience: workExperienceQuery.data ?? [],
        isBaseLoading: baseQuery.isPending,
        isBaseError: baseQuery.isError,
        baseError: baseQuery.error,
        isSectionLoading: activeSectionQuery?.isPending ?? false,
        isSectionError: activeSectionQuery?.isError ?? false,
        sectionError: activeSectionQuery?.error,
        refresh: () =>
            queryClient.invalidateQueries({
                queryKey: spouseProfileKeys.base(candidateId),
            }),
    };
}
